export interface ModelUsageEntry {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  costUSD: number;
}

export class CostTracker {
  private cumulativeCostUsd = 0;
  private modelUsage = new Map<string, ModelUsageEntry>();
  private seeded = false;

  /**
   * Rehydrate cumulative spend from the server so `maxBudgetUsd` enforcement
   * accounts for costs incurred by prior agent runs on the same task. Must be
   * called before any `addQueryCost` / `addModelUsage` — re-seeding after
   * costs have accumulated would clobber in-process totals.
   */
  seed(totalCostUsd: number, modelUsage: ModelUsageEntry[]): void {
    if (this.seeded) return;
    if (this.cumulativeCostUsd > 0 || this.modelUsage.size > 0) return;
    this.cumulativeCostUsd = totalCostUsd;
    for (const entry of modelUsage) {
      this.modelUsage.set(entry.model, { ...entry });
    }
    this.seeded = true;
  }

  /** Add cost from a completed query and return the running total */
  addQueryCost(queryCostUsd: number): number {
    this.cumulativeCostUsd += queryCostUsd;
    return this.cumulativeCostUsd;
  }

  /** Merge per-model usage from a completed query */
  addModelUsage(
    usage: Record<
      string,
      {
        inputTokens?: number;
        outputTokens?: number;
        cacheReadInputTokens?: number;
        cacheCreationInputTokens?: number;
        costUSD?: number;
      }
    >,
  ): void {
    for (const [model, data] of Object.entries(usage)) {
      const existing = this.modelUsage.get(model) ?? {
        model,
        inputTokens: 0,
        outputTokens: 0,
        cacheReadInputTokens: 0,
        cacheCreationInputTokens: 0,
        costUSD: 0,
      };
      existing.inputTokens += data.inputTokens ?? 0;
      existing.outputTokens += data.outputTokens ?? 0;
      existing.cacheReadInputTokens += data.cacheReadInputTokens ?? 0;
      existing.cacheCreationInputTokens += data.cacheCreationInputTokens ?? 0;
      existing.costUSD += data.costUSD ?? 0;
      this.modelUsage.set(model, existing);
    }
  }

  get totalCostUsd(): number {
    return this.cumulativeCostUsd;
  }

  get modelBreakdown(): ModelUsageEntry[] {
    return [...this.modelUsage.values()];
  }
}
